import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const CallCenterSurveyDash = () => {
    const navigate = useNavigate();
    const { appId, total_voters, language } = useAppId();
    const lang = language || 'en';

    // Translation dictionary
    const t = {
        en: {
            title: "Call Center Survey Dashboard",
            subtitle: "Overview of survey progress and reports.",
            totalVoters: "Total Voters",
            totalSurveyed: "Total Surveyed",
            done: "Done",
            unavailable: "Unavailable :",
            positive: "Positive",
            negative: "Negative",
            doubtful: "Doubtful",
            notInterested: "Can't say",
            otherReport: "OTHER REPORT",
            telecallerWise: "Telecaller Wise",
            dateWise: "Date Wise"
        },
        hi: {
            title: "कॉल सेंटर सर्वेक्षण डैशबोर्ड",
            subtitle: "सर्वेक्षण प्रगति और रिपोर्ट का अवलोकन।",
            totalVoters: "कुल मतदाता",
            totalSurveyed: "कुल सर्वेक्षित",
            done: "पूर्ण",
            unavailable: "अनुपलब्ध :",
            positive: "सकारात्मक",
            negative: "नकारात्मक",
            doubtful: "संदिग्ध",
            notInterested: "कह नहीं सकते",
            otherReport: "अन्य रिपोर्ट",
            telecallerWise: "टेलीकॉलर वार",
            dateWise: "तिथि वार"
        },
        gu: {
            title: "કોલ સેન્ટર સર્વે ડેશબોર્ડ",
            subtitle: "સર્વે પ્રગતિ અને રિપોર્ટ્સની ઝાંખી.",
            totalVoters: "કુલ મતદારો",
            totalSurveyed: "કુલ સર્વેક્ષણ",
            done: "પૂર્ણ",
            unavailable: "અનુપલબ્ધ :",
            positive: "હકારાત્મક",
            negative: "નકારાત્મક",
            doubtful: "શંકાસ્પદ",
            notInterested: "કહ नहीं सकते",
            otherReport: "અન્ય રિપોર્ટ",
            telecallerWise: "ટેલીકોલર વાઈઝ",
            dateWise: "તારીખ વાઈઝ"
        }
    };

    const txt = t[lang];

    // Initial State
    const [data, setData] = useState({
        totalVoters: 0,
        totalSurveyed: 0,
        pieData: [
            { name: txt.positive, value: 0, color: '#10b981' },
            { name: txt.negative, value: 0, color: '#ef4444' },
            { name: txt.doubtful, value: 0, color: '#f59e0b' },
            { name: txt.notInterested, value: 0, color: '#6366f1' },
        ],
        unavailable: 0,
    });

    // Update pieData when language changes
    useEffect(() => {
        setData(prev => ({
            ...prev,
            pieData: [
                { name: txt.positive, value: prev.pieData[0]?.value || 0, color: '#10b981' },
                { name: txt.negative, value: prev.pieData[1]?.value || 0, color: '#ef4444' },
                { name: txt.doubtful, value: prev.pieData[2]?.value || 0, color: '#f59e0b' },
                { name: txt.notInterested, value: prev.pieData[3]?.value || 0, color: '#6366f1' },
            ]
        }));
    }, [lang, txt.positive, txt.negative, txt.doubtful, txt.notInterested]);

    useEffect(() => {
        if (appId) {
            fetchData();
        }
    }, [appId]);

    const fetchData = async () => {
        try {
            const response = await axios.post(`${BASE_URL}/api/dis_survey_dash_for_admin`, {
                app_id: appId,
                survey_by:"Call Center"
            });

            if (response.data.success) {
                const res1 = response.data.data.result[0];
                const res2 = response.data.data.result2[0];

                const totalVoters = total_voters;
                const unavailable = res2?.not_available || 0;

                // Extract breakdown counts
                const p = res2?.p || 0;
                const n = res2?.n || 0;
                const d = res2?.d || 0;
                const c = res2?.c || 0;

                // Calculate Total Surveyed (Completed)
                const totalSurveyed = p + n + d + c;

                setData({
                    totalVoters: totalVoters,
                    totalSurveyed: totalSurveyed,
                    pieData: [
                        { name: txt.positive, value: p, color: '#10b981' },
                        { name: txt.negative, value: n, color: '#ef4444' },
                        { name: txt.doubtful, value: d, color: '#f59e0b' },
                        { name: txt.notInterested, value: c, color: '#6366f1' },
                    ],
                    unavailable: unavailable,
                });
            }
        } catch (error) {
            console.error("Error fetching call center survey dashboard data:", error);
        }
    };

    const handleNavigate = (path) => {
        navigate(path);
    };

    // Other Report Options
    const otherReportOptions = [
        { title: txt.telecallerWise, icon: '🎧', onClick: () => { handleNavigate('/telecaller_wise_survey_dash') } },
        { title: txt.dateWise, icon: '📅', onClick: () => { handleNavigate('/dis_date_wise_call_center_survey_dash') } },   
    ];

    // Custom Label Render
    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
        const RADIAN = Math.PI / 180;
        const radius = outerRadius + 20;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text x={x} y={y} fill="#334155" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize="12" fontWeight="600">
                {`${(percent * 100).toFixed(1)}%`}
            </text>
        );
    };

    return (
        <div className="app-container dashboard-page">
            <div className="dashboard-content container-fluid p-4">

                <Header title={txt.title} />

                <div className="row g-4 mb-5">

                    {/* --- Left Side: Stats and Chart --- */}
                    <div className="col-lg-5 col-md-12">
                        <div className="survey-card h-90 d-flex flex-column justify-content-between">

                            {/* Stats Header */}
                            <div className="top-stats-row">
                                <div className="stat-box">
                                    <span className="stat-label">{txt.totalVoters}</span>
                                    <span className="stat-value text-dark">{data.totalVoters.toLocaleString()}</span>
                                </div>
                                <div className="stat-divider"></div>
                                <div className="stat-box">
                                    <span className="stat-label">{txt.totalSurveyed}</span>
                                    <span className="stat-value text-dark">{data.totalSurveyed.toLocaleString()}</span>
                                </div>
                            </div>

                            {/* Chart Area */}
                            <div className="chart-wrapper">
                                <ResponsiveContainer width="100%" height={280}>
                                    <PieChart>
                                        <Pie
                                            data={data.pieData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={80}
                                            outerRadius={110}
                                            paddingAngle={0}
                                            dataKey="value"
                                            startAngle={90}
                                            endAngle={-270}
                                            label={renderCustomizedLabel}
                                            labelLine={true}
                                        >
                                            {data.pieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            formatter={(value) => value.toLocaleString()}
                                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>

                                {/* Center Text */}
                                <div className="chart-center-text">
                                    <span className="survey-percentage">
                                        {data.totalVoters > 0 ? ((data.totalSurveyed / data.totalVoters) * 100).toFixed(1) : 0}%
                                    </span>
                                    <span className="survey-label">{txt.done}</span>
                                </div>
                            </div>

                            {/* Unavailable Alert */}
                            <div className="unavailable-pill">
                                {txt.unavailable} <strong>{data.unavailable.toLocaleString()}</strong>
                            </div>

                            {/* Bottom Stats Grid */}
                            <div className="bottom-stats-grid">
                                {data.pieData.map((entry, index) => (
                                    <div key={index} className={`bottom-stat-item ${index !== data.pieData.length - 1 ? 'border-right' : ''}`}>
                                        <span className="b-stat-value" style={{ color: entry.color }}>
                                            {entry.value.toLocaleString()}
                                        </span>
                                        <span className="b-stat-label">{entry.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* --- Right Side: Other Report Section --- */}
                    <div className="col-lg-7 col-md-12">
                        <div className="survey-card">
                            <div className="pb-4">
                                <h4 className="section-title">{txt.otherReport}</h4>
                            </div>
                            <div className="row g-3">
                                {otherReportOptions.map((option, index) => (
                                    <div key={index} className="col-6 col-md-4">
                                        <div className="option-card" onClick={option.onClick}>
                                            <div className="option-icon">{option.icon}</div>
                                            <span className="option-text">{option.title}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <style jsx>{`
                /* Common Card Style */
                .survey-card {
                    background: white;
                    border-radius: 16px;
                    padding: 24px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.03);
                    border: 1px solid #f1f5f9;
                }

                /* --- Left Card Styles --- */
                .top-stats-row {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin-bottom: 20px;
                }
                .stat-box {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    min-width: 120px;
                }
                .stat-label {
                    font-size: 13px;
                    color: #64748b;
                    font-weight: 600;
                    margin-bottom: 4px;
                }
                .stat-value {
                    font-size: 20px;
                    font-weight: 800;
                }
                .stat-divider {
                    width: 1px;
                    height: 40px;
                    background-color: #e2e8f0;
                    margin: 0 20px;
                }

                .chart-wrapper {
                    position: relative;
                    margin: 10px 0 25px 0;
                }
                .chart-center-text {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    pointer-events: none;
                }
                .survey-percentage {
                    font-size: 22px;
                    font-weight: 800;
                    color: #0f172a;
                }
                .survey-label {
                    font-size: 12px;
                    color: #64748b;
                    font-weight: 600;
                    margin-top: 4px;
                }

                .unavailable-pill {
                    background-color: #fef2f2;
                    color: #991b1b;
                    font-weight: 500;
                    font-size: 14px;
                    text-align: center;
                    padding: 8px;
                    border-radius: 6px;
                    border: 1px solid #fecaca;
                    margin-bottom: 20px;
                }

                .bottom-stats-grid {
                    display: flex;
                    justify-content: space-between;
                    border-top: 1px solid #f1f5f9;
                    padding-top: 15px;
                }
                .bottom-stat-item {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                }
                .border-right {
                    border-right: 1px solid #e2e8f0;
                }
                .b-stat-value {
                    font-size: 18px;
                    font-weight: 800;
                    margin-bottom: 2px;
                }
                .b-stat-label {
                    font-size: 11px;
                    color: #64748b;
                    font-weight: 600;
                }

                .section-title {
                    font-size: 14px;
                    font-weight: 700;
                    color: #475569;
                    letter-spacing: 0.5px;
                    text-transform: uppercase;
                    margin: 0;
                }

                .option-card {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 25px 15px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    height: 100%;
                }
                .option-card:hover {
                    border-color: #3b82f6;
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
                    transform: translateY(-2px);
                }
                .option-icon {
                    font-size: 28px;
                    margin-bottom: 12px;
                }
                .option-text {
                    font-size: 13px;
                    font-weight: 600;
                    color: #1e293b;
                    text-align: center;
                    line-height: 1.4;
                }
            `}</style>
        </div>
    );
};

export default CallCenterSurveyDash;